import { useCallback, useMemo } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LineHistoryIcon from 'LuxKit/Icons/line/LineHistoryIcon'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import {
  bumpRecentCarHireSearch,
  getRecentCarHireSearchNavigation,
  markRecentCarHireSearchNavigation,
  type RecentCarHireSearch as RecentCarHireSearchType,
} from '../carHireRecentSearches'

const RecentSearchContainer = styled(Clickable)`
  display: flex;
  padding: ${rem(8)};
  flex-direction: column;
  gap: ${rem(8)};
  justify-content: flex-start;
  align-items: stretch;
  transition: background-color 0.2s;
  min-width: 100%;

  ${mediaQueryUp.tablet} {
    min-width: ${rem(350)};
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const IconBlock = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const SubHeader = styled(Caption)`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const SearchDetails = styled(Group)`
  flex: 1 1 auto;
  min-width: 0;
`

const DifferentLocationRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: ${rem(8)};
  align-items: center;
  width: 100%;
  max-width: 100%;
`

const LocationText = styled(BodyText)`
  min-width: 0;
`

interface Props {
  recentSearch: RecentCarHireSearchType
}

function formatDateRange(intl: IntlShape, recentSearch: RecentCarHireSearchType) {
  const pickUpDate = moment(recentSearch.pickUpDate).toDate()
  const dropOffDate = moment(recentSearch.dropOffDate).toDate()

  return intl.formatMessage(
    {
      id: '3J-KmSFdIrLKi9ZWrpC3',
      defaultMessage: '{pickUpDate} - {dropOffDate}',
      description: 'Date range displayed in a recent car hire search summary',
    },
    {
      pickUpDate: intl.formatDate(pickUpDate, { day: 'numeric', month: 'short' }),
      dropOffDate: intl.formatDate(dropOffDate, { day: 'numeric', month: 'short', year: 'numeric' }),
    },
  )
}

function formatTimeRange(intl: IntlShape, recentSearch: RecentCarHireSearchType) {
  const pickUpTime = moment(recentSearch.pickUpTime, 'HH:mm').toDate()
  const dropOffTime = moment(recentSearch.dropOffTime, 'HH:mm').toDate()

  return intl.formatMessage(
    {
      id: 'ZhSLi3yPN3UfgIgGiL2l',
      defaultMessage: '{pickUpTime} - {dropOffTime}',
      description: 'Time range displayed in a recent car hire search summary',
    },
    {
      pickUpTime: intl.formatTime(pickUpTime, { hour: 'numeric', minute: 'numeric' }),
      dropOffTime: intl.formatTime(dropOffTime, { hour: 'numeric', minute: 'numeric' }),
    },
  )
}

function RecentCarHireSearch({ recentSearch }: Props) {
  const intl = useIntl()
  const navigation = useMemo(() => getRecentCarHireSearchNavigation(recentSearch), [recentSearch])
  const isDifferentDropOff = recentSearch.pickUpId !== recentSearch.dropOffId || recentSearch.pickUpName !== recentSearch.dropOffName
  const separatorChar = intl.formatMessage(middleDotSeparatorMessage)
  const subHeader = [
    formatDateRange(intl, recentSearch),
    formatTimeRange(intl, recentSearch),
  ].filter(Boolean).join(` ${separatorChar} `)

  const onClickRecent = useCallback(() => {
    markRecentCarHireSearchNavigation(navigation)
    bumpRecentCarHireSearch(recentSearch)
  }, [navigation, recentSearch])

  return (
    <RecentSearchContainer to={navigation.url} onClick={onClickRecent}>
      <Group direction="horizontal" gap={12}>
        <IconBlock>
          <LineHistoryIcon size="M" />
        </IconBlock>
        <SearchDetails direction="vertical" gap={4}>
          {isDifferentDropOff && <DifferentLocationRow>
            <LocationText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate">{recentSearch.pickUpName}</LocationText>
            <LineArrowsSwapHorizontalIcon size="S" />
            <LocationText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate">{recentSearch.dropOffName}</LocationText>
          </DifferentLocationRow>}
          {!isDifferentDropOff && <LocationText variant="medium" colour="normal-contrast" weight="bold" wrap="truncate">{recentSearch.pickUpName}</LocationText>}
          <SubHeader wrap="pretty" variant="medium">{subHeader}</SubHeader>
        </SearchDetails>
      </Group>
    </RecentSearchContainer>
  )
}

export default RecentCarHireSearch
